import { Table } from '@/components/ui/';
import { useEffect, useMemo } from 'react';
import { useOrders } from '../api/get-orders';
import { FilledOrder, Order, useOrdersStore } from '@/store/use-orders-store';
import { OrderHistoryFilterType, OrderStatus } from '@/types/enums';
import { useShallow } from 'zustand/react/shallow';
import { ColumnDef } from '@tanstack/react-table';
import Empty from '@/components/ui/empty';
import { useTranslation } from 'react-i18next';
import { useAccountStore } from '@/store/use-account-store';
import OrderTypeComponent, {
  orderTypeAccessorFn,
} from './open-orders/order-type';
import Time, { timeAccessorFn } from './open-orders/time';
import SymbolCell, { symbolAccessorFn } from './open-orders/symbol';
import Status, { statusAccessorFn } from './orders-history/status';
import Fee, { feeAccessorFn } from './orders-history/fee';
import RealizedPnl, {
  realizedPnlAccessorFn,
  RealizedPnlHeader,
} from './orders-history/realized-pnl';
import Value, { valueAccessorFn } from './open-orders/value';
import SettledFunding, {
  settledFundingAccessorFn,
} from './orders-history/settled-funding';
import LastFilledPrice, { limitPriceAccessorFn } from './open-orders/price';
import Copyable from '@/components/ui/copyable';
import { truncateAddress } from '@/utils/format';
import {
  handleOrderForStore,
  orderResponseToOrder,
} from '@/utils/handle-store-data';

const filterFn = (filter: OrderHistoryFilterType, order: Order) => {
  if (filter === OrderHistoryFilterType.ALL) {
    return true;
  } else if (filter === OrderHistoryFilterType.FILLED) {
    return (
      order.status === OrderStatus.FILLED ||
      order.status === OrderStatus.PARTIALLY_FILLED
    );
  } else if (filter === OrderHistoryFilterType.REJECTED) {
    return order.status === OrderStatus.REJECTED;
  } else if (filter === OrderHistoryFilterType.CANCELLED) {
    return order.status === OrderStatus.CANCELLED;
  }
};

type Props = {
  filter: OrderHistoryFilterType;
  bgColor?: string;
  className?: string;
  noDataComponent?: React.ReactNode;
};

export default function OrdersHistoryTable({
  filter,
  bgColor,
  className,
  noDataComponent,
}: Props) {
  const { t } = useTranslation();
  const address = useAccountStore((state) => state.address);

  const data = useOrdersStore(
    useShallow((state) => {
      const { filledOrderIds, cancelledOrderIds, rejectedOrderIds, orders } =
        state.getOrders(address);
      return Array.from(
        filledOrderIds.union(rejectedOrderIds).union(cancelledOrderIds),
      )
        .filter((id) => filterFn(filter, orders[id]))
        .sort((a, b) => {
          const { status: aStatus } = orders[a];
          const { status: bStatus } = orders[b];
          const aTime =
            aStatus === OrderStatus.FILLED ||
            aStatus === OrderStatus.PARTIALLY_FILLED
              ? (orders[a] as FilledOrder).lastFilledTime
              : orders[a].postTime;
          const bTime =
            bStatus === OrderStatus.FILLED ||
            bStatus === OrderStatus.PARTIALLY_FILLED
              ? (orders[b] as FilledOrder).lastFilledTime
              : orders[b].postTime;
          return aTime - bTime;
        });
    }),
  );

  const columns = useMemo<ColumnDef<string>[]>(
    () => [
      {
        id: 'time',
        accessorFn: timeAccessorFn,
        cell: ({ row }) => <Time orderId={row.original} />,
        header: t('ordersHistoryTable.time'),
        meta: { width: 'w-[12%] min-w-60' },
      },
      {
        accessorFn: orderTypeAccessorFn,
        cell: ({ row }) => <OrderTypeComponent orderId={row.original} />,
        sortUndefined: 'last',
        header: t('ordersHistoryTable.type'),
        meta: { width: 'w-[10%] min-w-36' },
      },
      {
        accessorFn: symbolAccessorFn,
        cell: ({ row }) => (
          <SymbolCell orderId={row.original} skipSubscription />
        ),
        header: t('ordersHistoryTable.market'),
        meta: { width: 'w-[10%] min-w-52' },
      },
      // {
      //   accessorFn: sizeAccessorFn,
      //   cell: ({ row }) => <Size orderId={row.original} />,
      //   header: t('ordersHistoryTable.size'),
      //   meta: { width: 'w-[10%] min-w-44' },
      // },
      {
        accessorFn: valueAccessorFn,
        cell: ({ row }) => <Value orderId={row.original} />,
        header: t('ordersHistoryTable.value'),
        sortUndefined: 'last',
        meta: { width: 'w-[12%] min-w-44' },
      },
      {
        accessorFn: limitPriceAccessorFn,
        cell: ({ row }) => <LastFilledPrice orderId={row.original} />,
        header: t('ordersHistoryTable.fillPrice'),
        sortUndefined: 'last',
        meta: { width: 'w-[12%] min-w-44' },
      },
      {
        accessorFn: settledFundingAccessorFn,
        cell: ({ row }) => <SettledFunding orderId={row.original} />,
        header: t('ordersHistoryTable.funding'),
        meta: { width: 'w-[10%] min-w-44' },
        sortUndefined: 'last',
        sortDescFirst: false,
      },
      {
        accessorFn: feeAccessorFn,
        cell: ({ row }) => <Fee orderId={row.original} />,
        header: t('ordersHistoryTable.fee'),
        meta: { width: 'w-[10%] min-w-44' },
        sortUndefined: 'last',
        sortDescFirst: false,
      },
      {
        id: 'realizedPnl',
        accessorFn: realizedPnlAccessorFn,
        cell: ({ row }) => <RealizedPnl orderId={row.original} />,
        header: RealizedPnlHeader,
        meta: { width: 'w-[12%] min-w-52' },
        sortUndefined: 'last',
        sortDescFirst: false,
      },
      {
        accessorFn: statusAccessorFn,
        cell: ({ row }) => <Status orderId={row.original} />,
        header: t('ordersHistoryTable.status'),
        meta: { width: 'w-[10%] min-w-44' },
      },
      {
        id: 'id',
        cell: ({ row }) =>
          row.original ? (
            <div className="mt-3 flex items-center gap-2">
              <div>{truncateAddress(row.original)}</div>
              <Copyable>{row.original}</Copyable>
            </div>
          ) : (
            <div className="mt-3">-</div>
          ),
        header: t('ordersHistoryTable.id'),
        meta: {
          width: 'w-[10%] min-w-[130px] flex justify-end',
        },
      },
    ],
    [t],
  );

  const { data: orders, isLoading } = useOrders();

  useEffect(() => {
    if (!orders) return;
    orders.forEach((res) => {
      const order = orderResponseToOrder(res);
      handleOrderForStore(order, { skipFill: true });
    });
  }, [orders]);

  return (
    <Table
      id="orders-history"
      data={data}
      columns={columns}
      loading={isLoading}
      initialSorting={[{ id: 'time', desc: true }]}
      isInfiniteScroll
      infiniteScrollOverscan={10}
      infiniteScrollEstimateRowSize={57}
      disableDelay
      bgColor={bgColor}
      className={className}
      emptyComponent={
        noDataComponent || (
          <Empty title={t('ordersHistoryTable.noOrdersFound')} />
        )
      }
    />
  );
}
